import { Box, Typography, Grid, Card, CardContent, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import { StringKeys } from '../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';

const FeatureCard = ({ data, index }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
    >
      <Card
        elevation={0}
        sx={{
          background: 'rgba(255, 255, 255, 0.05)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          borderRadius: '24px',
          overflow: 'visible',
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <CardContent sx={{ p: 4, position: 'relative' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 700,
                mr: 2,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {data.title}
            </Typography>
            
            <Box
              className="icon-container"
              sx={{
                width: '36px',
                height: '36px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'transform 0.3s ease-in-out',
                transform: 'scale(0.7)',
              }}
            >
              {data.icon}
            </Box>
          </Box>

          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ 
              lineHeight: 1.7,
              opacity: 0.85
            }}
          >
            {data.description}
          </Typography>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const FeaturesSection = ({ cardsData, handleNext, handlePrev, visibleCards }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();
  
  // Auto-rotation effect
  useEffect(() => {
    const autoRotationInterval = setInterval(() => {
      handleNext();
    }, 5000); // Rotate every 5 seconds
    
    return () => {
      clearInterval(autoRotationInterval);
    };
  }, [handleNext]);
  
  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
        pt: 4,
        px: 4,
      }}
    >
      {/* Background decoration */}
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '100%',
          height: '100%',
          zIndex: 0,
        }}
      />

      {/* Content */}
      <Box sx={{ position: 'relative', maxWidth: '1200px' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <Typography 
            variant="h3" 
            align="center" 
            gutterBottom
            sx={{ 
              fontWeight: 800,
              background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.dark})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            {t(StringKeys.LANDING_PAGE_CAROUSEL_HEADER)}
          </Typography>
        </motion.div>

        <Grid container spacing={4}>
          {visibleCards.map((idx) => (
            <Grid item xs={12} sm={6} md={4} key={idx}>
              <FeatureCard data={cardsData[idx]} index={idx} />
            </Grid>
          ))}
        </Grid>

        {/* Navigation buttons removed as per requirements */}
      </Box>
    </Box>
  );
};

export default FeaturesSection;